/**
 * Schedule Master Screen — BSSchedule CRUD
 * Left: Form | Right: Chart of Accounts Tree
 * Fields: Schedule No (auto), Schedule Name*, Category (BS/PL radio)
 * PlaceOfHead is auto-filled by API/SP — not editable from frontend
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Save, Grid3X3, XCircle, Plus, RefreshCw, ArrowLeft } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import ChartOfAccountsTree from '../../components/erp/ChartOfAccountsTree';
import DynamicFieldRenderer from '../../components/erp/DynamicFieldRenderer';
import { erpToast } from '../../components/erp/ERPToast';
import { FormHelp } from '../../components/help-engine/FormHelp';
import { HelpInfo } from '../../components/help-engine/HelpInfo';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL, getToken} from '../../lib/api';

const token = () => getToken() || '';
const hdrs = () => ({ Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' });

const ScheduleMasterScreen = ({ theme = 'light', navigateTo = () => {} }) => {
  const [view, setView] = useState('grid');
  const [allItems, setAllItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ ScheduleNo: '', ScheduleName: '', Category: 'BS' });
  const [mode, setMode] = useState('add'); // add, edit, view
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [treeKey, setTreeKey] = useState(0);
  const token = getToken() || '';
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();
  const isSuperAdmin = userRole === 'SUPER_ADMIN' || userRole === 'admin' || userRole === 'ADMINSUPPORT' || userRole === 'LICENSOR';
  const [dynValues, setDynValues] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Use /api/finance/schedules — .NET-first with MSSQL fallback
      const r = await fetch(`${API_URL}/api/finance/schedules`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) { setAllItems(j.data || []); }
    } catch { erpToast.error('Failed to load schedules'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const COLUMNS = useMemo(() => [
    { field: 'Scheduleno', header: 'Schedule No' },
    { field: 'ScheduleName', header: 'Schedule Name' },
    { field: 'ScheduleType', header: 'Category' },
    { field: 'Status', header: 'Status' },
  ], []);

  const { data, filteredTotal } = useMemo(() => {
    let items = [...allItems];
    // Apply column filters
    Object.entries(filters).forEach(([key, val]) => {
      if (val) { const s = val.toLowerCase(); items = items.filter(i => String(i[key] || '').toLowerCase().includes(s)); }
    });
    const filteredTotal = items.length;
    // Sort
    if (sortField) {
      items.sort((a, b) => {
        const av = String(a[sortField] || ''), bv = String(b[sortField] || '');
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    // Paginate
    const data = items.slice((page - 1) * pageSize, page * pageSize);
    return { data, filteredTotal };
  }, [allItems, filters, sortField, sortDir, page, pageSize]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const isView = mode === 'view';

  const openForm = (item, m) => {
    setForm(item ? { ScheduleNo: item.Scheduleno, ScheduleName: item.ScheduleName, Category: item.ScheduleType || 'BS' }
      : { ScheduleNo: '', ScheduleName: '', Category: 'BS' });
    setMode(m || 'add');
    setView('form');
    setError('');
  };

  const handleSave = async () => {
    if (!form.ScheduleName?.trim()) { setError('Schedule Name is required'); return; }
    setSaving(true); setError('');
    try {
      const isEdit = mode === 'edit' && form.ScheduleNo;
      const url = isEdit
        ? `${API_URL}/api/finance/schedules?scheduleId=${encodeURIComponent(form.ScheduleNo)}`
        : `${API_URL}/api/finance/schedules`;
      const method = isEdit ? 'PUT' : 'POST';
      const body = {
        ScheduleName: form.ScheduleName,
        ScheduleType: form.Category || 'BS',
        ...(isEdit ? { scheduleId: form.ScheduleNo } : {})
      };
      const r = await fetch(url, { method, headers: hdrs(), body: JSON.stringify(body) });
      const j = await r.json();
      if (j.success) {
        erpToast.success(j.message || (isEdit ? 'Schedule updated' : 'Schedule saved'));
        if (j.Scheduleno) setForm(p => ({ ...p, ScheduleNo: j.Scheduleno }));
        if (isEdit) setMode('add');
        setTreeKey(k => k + 1);
        loadData();
      } else { setError(j.error || 'Save failed'); }
    } catch (e) { setError('Failed to save schedule'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Void this schedule?')) return;
    try {
      const r = await fetch(`${API_URL}/api/finance/schedules?scheduleId=${encodeURIComponent(id)}`, { method: 'DELETE', headers: hdrs() });
      const j = await r.json();
      if (j.success) { erpToast.success('Schedule voided'); loadData(); setTreeKey(k => k + 1); }
      else erpToast.error(j.error || 'Void failed');
    } catch { erpToast.error('Failed to void schedule'); }
  };

  // ─── GRID ───
  if (view === 'grid') {
    return (
      <div className="erp-module" style={{ flexDirection: 'row' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <ERPAdvancedGrid
            title="Schedule Master"
            columns={COLUMNS}
            data={data}
            total={filteredTotal}
            idField="Scheduleno"
            nameField="ScheduleName"
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            filters={filters}
            onFilterChange={(f) => { setFilters(f); setPage(1); }}
            loading={loading}
            onAddNew={() => openForm(null, 'add')}
            onEdit={(row, m) => openForm(row, m)}
            onVoid={(row) => handleDelete(row.Scheduleno)}
            testId="schedule-master"
            onBack={() => navigateTo('HUB')}
            exportFileName="ScheduleMaster"
            cellRenderer={(field, value, row) => {
              if (field === 'ScheduleType') return value === 'BS' ? 'Balance Sheet' : value === 'PL' ? 'Profit & Loss' : (value || '—');
              if (field === 'Status') return <span className={`erp-status-badge ${value === 'S' ? 'active' : 'inactive'}`}>{value === 'S' ? 'Active' : 'Inactive'}</span>;
              return null;
            }}
          />
        </div>
        <div style={{ width: 280, flexShrink: 0 }}><ChartOfAccountsTree refreshKey={treeKey} /></div>
      </div>
    );
  }

  // ─── FORM ───
  return (
    <FormHelp formName="ScheduleMaster" userRole={userRole}>
    <div className="erp-module" style={{ flexDirection: 'row' }} data-dev-component="ScheduleMaster" data-dev-file="screens/finance/ScheduleMasterScreen.js">
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div className="erp-screen-header">
          <button onClick={() => setView('grid')} data-testid="screen-back-btn" style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"2px 0"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 5-7 7 7 7"/></svg><span className="erp-screen-title" style={{color:"var(--erp-accent,#1856a7)"}}>Schedule Master</span></button>
          <div style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--erp-font-mono)', color: 'var(--erp-primary)' }}>
            {form.ScheduleNo || 'New'}
          </div>
          <div className="erp-screen-actions">
            {!isView && <button data-testid="schedule-save-btn" className="erp-btn erp-btn-ghost" onClick={handleSave} disabled={saving}>{mode === 'edit' ? <RefreshCw size={13} /> : <Save size={13} />} {saving ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}</button>}
            <button className="erp-btn erp-btn-ghost" onClick={() => { setView('grid'); loadData(); }}><Grid3X3 size={13} /> Gridview</button>
            {!isView && <button className="erp-btn erp-btn-ghost" onClick={() => openForm(null, 'add')}><XCircle size={13} /> Clear</button>}
          </div>
        </div>
        {error && <div className="erp-alert-error">{error}</div>}
        <form style={{ padding: 'var(--erp-sp-4)', flex: 1 }} onSubmit={e => e.preventDefault()}>
          <div className="erp-form-card"><div className="erp-form-section">
            <div style={{ maxWidth: 400 }}>
              <div className="erp-form-row">
                <label className="erp-form-label">Schedule No<HelpInfo controlId="ScheduleNo" controlName="Schedule No" /></label>
                <div className="erp-form-control"><input data-testid="sch-code" data-help-control="ScheduleNo" value={form.ScheduleNo} disabled className="erp-input" style={{ fontFamily: 'var(--erp-font-mono)' }} /></div>
              </div>
              <div className="erp-form-row">
                <label className="erp-form-label required">Schedule Name<HelpInfo controlId="ScheduleName" controlName="Schedule Name" /></label>
                <div className="erp-form-control"><input data-testid="sch-name" data-help-control="ScheduleName" value={form.ScheduleName} onChange={e => set('ScheduleName', e.target.value)} disabled={isView} className="erp-input" /></div>
              </div>
              <div className="erp-form-row">
                <label className="erp-form-label">Category<HelpInfo controlId="Category" controlName="Category" /></label>
                <div className="erp-form-control" style={{ display: 'flex', gap: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    <input type="radio" name="sch-cat" data-help-control="Category" value="BS" checked={form.Category === 'BS'} onChange={() => set('Category', 'BS')} disabled={isView} /> Balance Sheet
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    <input type="radio" name="sch-cat" data-help-control="Category" value="PL" checked={form.Category === 'PL'} onChange={() => set('Category', 'PL')} disabled={isView} /> Profit & Loss
                  </label>
                </div>
              </div>
              <DynamicFieldRenderer screenName="ScheduleMaster" recordId={form.ScheduleNo} token={token} values={dynValues} onChange={(k, v) => setDynValues(p => ({ ...p, [k]: v }))} readOnly={isView} userRole={userRole} />
            </div>
          </div></div>
        </form>
      </div>
      <div style={{ width: 280, flexShrink: 0 }}><ChartOfAccountsTree refreshKey={treeKey} /></div>
    </div>
    </FormHelp>
  );
};

export default ScheduleMasterScreen;
